import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { axiosInstance } from "@/lib/api";
import { queue } from "@/components/Toast";
import type {
  RevokeStrategyShareResult,
  SendStrategySharesResult,
  StrategyGuestShare,
  StrategySharesState,
} from "@/types/strategyShare.type";

/** Keyed under the brand so a change to its guest list can refresh every roster. */
const sharesKey = (brandId: string | null, strategyId: string | null) => [
  "strategyShares",
  brandId,
  strategyId,
];

function errorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    return (err.response?.data as { message?: string })?.message ?? err.message ?? fallback;
  }
  return fallback;
}

function stateOf(result: StrategySharesState): StrategySharesState {
  return {
    shareable: result.shareable,
    status: result.status,
    shares: result.shares,
    guests: result.guests,
  };
}

/** Enabled only while the share modal is open — nothing else reads this. */
export function useStrategyShares(
  brandId: string | null,
  strategyId: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: sharesKey(brandId, strategyId),
    queryFn: async () => {
      const res = await axiosInstance.get<StrategySharesState>(
        `/social-strategies/${brandId}/${strategyId}/shares`,
      );
      return res.data;
    },
    enabled: !!brandId && !!strategyId && (options?.enabled ?? true),
  });
}

/**
 * Send the strategy to some of the brand's guests. Each one shows as sent
 * straight away; a failure takes back only the rows this send added.
 */
export function useSendStrategyShares(brandId: string | null, strategyId: string | null) {
  const qc = useQueryClient();
  const key = sharesKey(brandId, strategyId);
  return useMutation({
    mutationFn: async (userIds: string[]) => {
      const res = await axiosInstance.post<SendStrategySharesResult>(
        `/social-strategies/${brandId}/${strategyId}/shares`,
        { userIds },
      );
      return res.data;
    },
    onMutate: async (userIds) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<StrategySharesState>(key);
      if (!prev) return { added: [] as string[] };
      const already = new Set(prev.shares.map((s) => s.userId));
      const now = new Date().toISOString();
      const added: StrategyGuestShare[] = prev.guests
        .filter((g) => userIds.includes(g.userId) && !already.has(g.userId))
        .map((g) => ({ ...g, sentAt: now, sentBy: "", lastViewedAt: null }));
      qc.setQueryData<StrategySharesState>(key, {
        ...prev,
        shares: [
          ...added,
          ...prev.shares.map((s) => (userIds.includes(s.userId) ? { ...s, sentAt: now } : s)),
        ],
      });
      return { added: added.map((s) => s.userId) };
    },
    onError: (err, _userIds, ctx) => {
      const added = new Set(ctx?.added ?? []);
      qc.setQueryData<StrategySharesState>(key, (prev) =>
        prev ? { ...prev, shares: prev.shares.filter((s) => !added.has(s.userId)) } : prev,
      );
      queue.add({
        title: "Could not send the strategy",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
    onSuccess: (result) => {
      qc.setQueryData<StrategySharesState>(key, stateOf(result));
      if (result.failed === 0) {
        queue.add({
          title: result.notified === 1 ? "Strategy sent" : `Sent to ${result.notified}`,
          description: "It's in their account under Strategy, and they've been emailed a link to it.",
          variant: "success",
        });
      } else {
        queue.add({
          title: `${result.failed} of ${result.failed + result.notified} emails didn't send`,
          description: `It's in their account either way. Refused: ${result.failures.map((f) => f.email).join(", ")}`,
          variant: "warning",
        });
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}

/** Take the strategy back from one guest. */
export function useRevokeStrategyShare(brandId: string | null, strategyId: string | null) {
  const qc = useQueryClient();
  const key = sharesKey(brandId, strategyId);
  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await axiosInstance.post<RevokeStrategyShareResult>(
        `/social-strategies/${brandId}/${strategyId}/shares/revoke`,
        { userId },
      );
      return res.data;
    },
    onMutate: async (userId) => {
      await qc.cancelQueries({ queryKey: key });
      const removed = qc.getQueryData<StrategySharesState>(key)?.shares.find((s) => s.userId === userId);
      qc.setQueryData<StrategySharesState>(key, (prev) =>
        prev ? { ...prev, shares: prev.shares.filter((s) => s.userId !== userId) } : prev,
      );
      return { removed };
    },
    onError: (err, _userId, ctx) => {
      const removed = ctx?.removed;
      if (removed) {
        qc.setQueryData<StrategySharesState>(key, (prev) =>
          prev && !prev.shares.some((s) => s.userId === removed.userId)
            ? { ...prev, shares: [...prev.shares, removed] }
            : prev,
        );
      }
      queue.add({
        title: "Could not take the strategy back",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
    onSuccess: (result) => {
      qc.setQueryData<StrategySharesState>(key, stateOf(result));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}
